import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { pathwayStepLabel } from "@/lib/pathwayFamilies";
import type { NamedRow } from "@/lib/namedParts";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Tooltip } from "@/ui/Tooltip";

/**
 * A Pathway's steps, in clinical order, with ordering controls that survive
 * being used (#643).
 *
 * ## Why this exists beside `NamedRowList`
 *
 * `NamedRowList` is #492's list for main concern, and its Move up / Move down
 * pair is the right CONTROL — two buttons are operable by keyboard, pointer and
 * assistive technology on the first day, which is #455's requirement and
 * `PartsEditor`'s existing reasoning. Two things it does not do, and both are
 * about what happens AFTER the press:
 *
 *   * **The clinician is left holding an inert control.** Moving the second row
 *     up makes the Move up they just pressed the first row's, which is refused.
 *     Focus is not LOST — `Button` renders `disabled` as `aria-disabled` on
 *     purpose, so every refused control in this application stays focusable and
 *     keeps its reason discoverable — but a keyboard user continuing to reorder
 *     is now pressing a button that answers nothing, with no indication of
 *     which control to move to. THIS list moves focus to the row's other
 *     direction button: the pair's remaining operable half, and the one that
 *     undoes the move. That is the WAI-ARIA APG's guidance for a reorder pair,
 *     and it is a change of destination rather than a rescue from `body`.
 *   * **Nothing is announced.** The rows re-render with new numbers, which a
 *     sighted clinician sees and a screen-reader user does not: the DOM AROUND
 *     the focused button changed, not the button, and its accessible name is
 *     deliberately position-free so it is not re-announced. A polite live
 *     region states the outcome in the terms the list is ordered by — name, new
 *     position, and the total.
 *
 * THE PRESSED CONTROL OTHERWISE KEEPS FOCUS BY ITSELF, and this list does not
 * re-take it. Rows are keyed on `role`, which a move does not change, so React
 * relocates the existing DOM nodes rather than replacing them and the browser's
 * focus rides along. Restoring focus in that case would be a no-op that could
 * only ever go wrong.
 *
 * The two lists are deliberately not merged. `NamedRowList` serves a family
 * this ticket does not own, and rewriting the component the main-concern
 * surface depends on, from a ticket scoped to Pathways, is how a scoped change
 * becomes an unreviewable one — the same reasoning `useNamedDraft` gives for
 * not refactoring `AssetEditor`. Reconciling them is a follow-up named in this
 * pull request rather than left to be discovered.
 *
 * ## What the rows say, and why
 *
 * A step the SERVICE calls `protected` is ENGINE-OWNED and carries no editor:
 * `PathwayProtectedStep` publishes identity, order and
 * `reason: "semantic_schema_unavailable"` and deliberately no data field, so
 * this list marks it protected and offers Open as a read-only inspection rather
 * than hiding it. Hiding it would misstate the version — the step is in the
 * content every adopting project inherits, and its POSITION is part of the
 * clinical order being edited.
 *
 * NOT "structured", SINCE #911. Two structured roles — `screening_steps` and
 * `screening_diagnosis` — now publish a typed schema and arrive as
 * `editability: "typed"`, so the document kind stopped answering the question
 * and the server's own verdict answers it instead. Their form is #912's; what
 * this list must not do in the meantime is badge them locked.
 *
 * REORDERING A PROTECTED STEP IS STILL ALLOWED, and that is a considered
 * position rather than an oversight. Order is `parts` order, which the service
 * accepts on any part; what this editor may not do is author the INSIDE of an
 * engine-owned document. Refusing to move it would leave a clinician unable to
 * put an authorable step before one, with nothing on the surface able to
 * explain why.
 */

export function PathwayStepList({
  rows,
  typedRoles,
  requiredRoles,
  manifestUnread,
  readOnly,
  onOpen,
  onMove,
  onRemove,
  add,
}: {
  /**
   * The steps as named rows, in stored order.
   *
   * `NamedRow` rather than the contract's `PathwayStep`, deliberately: this
   * list is rendered over an OPEN DRAFT, whose order the clinician is changing,
   * and a draft's rows are `namedParts`' projection of the parts array. The
   * read-only contract shape is what the catalog renders; the two agree because
   * a step's `id` IS its part key and its `order` IS the part's index.
   */
  rows: readonly NamedRow[];
  /**
   * The roles the SERVICE publishes a typed schema for (#912).
   *
   * A draft row carries no `editability` — it is `namedParts`' projection, and
   * its only vocabulary is the part kind — so over a draft "structured" stopped
   * answering "is this engine-owned" when #911 published two schemas. The
   * caller reads the answer off the part manifest and passes it here; an empty
   * set (a manifest still loading, or one whose read failed) leaves every
   * structured row on its existing protected presentation, which is the safe
   * direction.
   *
   * REQUIRED, not optional. It was optional in the first round, and every read
   * of it was `typedRoles?.has(...)` — so a caller that simply forgot the prop
   * got the pre-#911 presentation (both typed rows badged locked, both Remove
   * controls refused) with no type error and no failing hermetic test. The
   * silent-regression path is worth more than the convenience.
   */
  typedRoles: ReadonlySet<string>;
  /**
   * The roles this asset type's manifest makes REQUIRED.
   *
   * A required role cannot be removed however authorable its document is:
   * `library.validate_parts` refuses a version missing it
   * (`pathway requires the parts for 'he': screening_steps`). #911 published a
   * schema for `screening_steps`, which is also the one structured role
   * `_PART_MANIFEST` puts in `pathway`'s required family — so "typed" and
   * "removable" are NOT the same question, and answering only the first left
   * Remove enabled on a role whose removal can never be saved.
   */
  requiredRoles: ReadonlySet<string>;
  /**
   * Whether the manifest read failed or has not settled.
   *
   * TRUE WHILE PENDING TOO, not only on a failure. Both rules this list mirrors
   * — required families and protected documents — are the manifest's, so until
   * it arrives neither can be evaluated and no removal may be allowed or
   * refused on their authority. Saying "the service has not said" covers all
   * three states honestly; asserting that the pinned engine owns a row, from a
   * roster that has not arrived, does not.
   */
  manifestUnread: boolean;
  readOnly: boolean;
  onOpen: (role: string) => void;
  /** Move the row at `from` to `to`. Both are indices into `rows`. */
  onMove: (role: string, to: number) => void;
  onRemove: (role: string) => void;
  /** The add control, when the caller puts one under the list. */
  add?: {
    label: string;
    /** Why adding is refused, or `null`. Never an unexplained disabled button. */
    refusal: string | null;
    onAdd: () => void;
  };
}) {
  // WHICH control should hold focus once the moved order has rendered, keyed by
  // the row's ROLE rather than by its index — the index is exactly the thing
  // that just changed, so a queued index would restore focus onto whichever row
  // moved into the old slot.
  //
  // A REF, NOT STATE. Nothing renders from it: it is an instruction to the DOM,
  // consumed once by the effect below and cleared there. State would mean a
  // second render per move whose only job is to forget the instruction, and
  // clearing it inside the effect is the cascading `setState` the React hooks
  // lint rule correctly refuses.
  const restore = useRef<{ role: string; direction: "up" | "down" } | null>(
    null,
  );
  // The last move, as a sentence. Held in state rather than derived, because
  // the live region must announce a MOVE and not the mere fact that the list
  // has an order — a derived string would re-announce on every unrelated
  // re-render of the draft.
  const [announcement, setAnnouncement] = useState("");
  const controls = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    const pending = restore.current;
    if (pending === null) {
      return;
    }
    restore.current = null;
    const index = rows.findIndex((row) => row.role === pending.role);
    if (index === -1) {
      // The row left the list between the move and this effect — somebody
      // else's version landed and re-seeded the draft. Nothing to restore, and
      // guessing a neighbour would move focus somewhere the clinician did not
      // ask for.
      return;
    }
    // ONLY when the pressed control has become inert. While it is still
    // operable the browser has already kept focus on it — the row's DOM node
    // moved rather than being replaced — and calling `focus()` on the element
    // that already has it would be a no-op with a way to go wrong.
    const stillOperable =
      pending.direction === "up" ? index > 0 : index < rows.length - 1;
    if (!stillOperable) {
      const opposite = pending.direction === "up" ? "down" : "up";
      controls.current.get(`${pending.role}:${opposite}`)?.focus();
    }
  }, [rows]);

  /**
   * Why this row cannot be removed, or `null`.
   *
   * TWO independent service rules, and a row only has to trip one:
   *
   * `validate_parts` refuses a version that omits a REQUIRED role, whatever its
   * kind — this catches `screening_prompt` and `screening_steps` on a Pathway,
   * and it is the rule the typed check alone missed. `screening_steps` is both
   * typed and required, so "the clinician may author it" and "a version may
   * drop it" are different answers about the same row.
   *
   * `validate_protected_documents` refuses a version that drops a structured
   * role the service publishes no schema for.
   *
   * REORDERING every row stays allowed either way: order is `parts` order, and
   * neither rule is about position.
   */
  function removalRefusal(row: NamedRow): string | null {
    // NO MANIFEST, NO REMOVAL — of ANY row, not just structured ones.
    //
    // Both rules this function mirrors are read off the manifest, so without it
    // neither can be evaluated. Refusing only structured rows left the hole the
    // required check was added to close, one door over: `screening_prompt` is in
    // `pathway`'s required family and is a PROMPT row, so it short-circuited to
    // `null` and stayed removable during exactly the read failure that makes
    // `requiredRoles` empty — and Add is refused in that state too, so the
    // draft could not even be repaired.
    if (manifestUnread) {
      return "The Editor Service has not reported which steps this asset type allows, so no step can be removed until that read succeeds.";
    }
    if (requiredRoles.has(row.role)) {
      return `${row.label} is required for this asset type, so a new version cannot drop it.`;
    }
    if (row.kind !== "structured" || typedRoles.has(row.role)) {
      return null;
    }
    return "This document is owned by the pinned engine. A new version cannot drop it, so it cannot be removed here.";
  }

  function move(row: NamedRow, index: number, direction: "up" | "down") {
    const to = direction === "up" ? index - 1 : index + 1;
    restore.current = { role: row.role, direction };
    setAnnouncement(
      `${row.label} moved to position ${to + 1} of ${rows.length}.`,
    );
    onMove(row.role, to);
  }

  return (
    <div className="space-y-3">
      {/* Mounted unconditionally and empty until a move happens: a live region
          added to the DOM at the same moment it gains text is frequently not
          announced at all, because the assistive technology never observed the
          node being empty first. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <ol
        aria-label="Steps, in clinical order"
        className="overflow-hidden rounded-md border border-border bg-card"
      >
        {rows.map((row, index) => {
          // COMPUTED ONCE per row: the control's disabled state and the reason
          // it states must be the same answer, and the tooltip previously asked
          // the question a second time.
          const refusal = removalRefusal(row);
          return (
          <li
            key={row.role}
            className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
          >
            <span className="w-6 shrink-0 text-xs tabular-nums text-muted-foreground">
              {index + 1}.
            </span>

            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <span className="min-w-0 truncate text-sm text-foreground">
                {row.label}
              </span>
              {row.kind === "structured" && !typedRoles.has(row.role) ? (
                // The badge says what the contract says: this document's
                // semantic schema is not published, so it is shown and ordered
                // but never authored here. A role the service DOES publish a
                // schema for is excluded — badging it locked beside a row that
                // opens a working form would contradict the form.
                <Badge tone="outline">
                  <Lock aria-hidden="true" className="mr-1 h-3 w-3" />
                  Clinical engine document
                </Badge>
              ) : null}
              {row.documents.length > 1 ? (
                // A read-only fact, shown only where the step is genuinely
                // stored in more than one language and two rows would otherwise
                // be indistinguishable. Never a control that authors a prefix.
                <span className="text-xs text-muted-foreground">
                  {row.documents
                    .map((document) => document.language ?? "all")
                    .join(" · ")}
                </span>
              ) : null}
            </span>

            <Button variant="ghost" size="sm" onClick={() => onOpen(row.role)}>
              Open
            </Button>

            {readOnly ? null : (
              <div className="flex items-center gap-0.5">
                {/* THE STEP'S OWN NAME IS IN EVERY LABEL. The audit's PW-LIB-09
                    is "repeated ambiguous buttons": six identical "Move up"
                    controls give a screen-reader user a list of six
                    indistinguishable items. The position is deliberately NOT in
                    the label — it changes on every move, and a name that
                    changes under a focused control is re-announced as a
                    different button. */}
                <IconButton
                  ref={(element) => {
                    const key = `${row.role}:up`;
                    if (element) controls.current.set(key, element);
                    else controls.current.delete(key);
                  }}
                  label={`Move ${row.label} up`}
                  icon={<ArrowUp className="h-4 w-4" />}
                  disabled={index === 0}
                  onClick={() => move(row, index, "up")}
                />
                <IconButton
                  ref={(element) => {
                    const key = `${row.role}:down`;
                    if (element) controls.current.set(key, element);
                    else controls.current.delete(key);
                  }}
                  label={`Move ${row.label} down`}
                  icon={<ArrowDown className="h-4 w-4" />}
                  disabled={index === rows.length - 1}
                  onClick={() => move(row, index, "down")}
                />
                {/* REMOVAL IS A PROTECTED-DOCUMENT WRITE TOO. #911's
                    `validate_protected_documents` refuses a new version that
                    DROPS an engine-owned document, because the next exact pin
                    then stops emitting that file and the project silently falls
                    back to the inherited base — an effective runtime change at
                    a role the catalog calls engine-owned. So the control states
                    the refusal instead of leading to a rejected save. */}
                {/* ONE TOOLTIP, NOT TWO. `IconButton` wraps itself in a
                    `Tooltip` carrying its label unless `showTooltip` is false,
                    so nesting this one around it anchored two Radix tooltips to
                    the same trigger: both render, both at `side="bottom"`, and
                    the button's `aria-describedby` names only one of them.
                    `LibraryCatalog` already spells the correct form for exactly
                    this refusal-or-label pattern. */}
                <Tooltip content={refusal ?? `Remove ${row.label}`}>
                  <IconButton
                    label={`Remove ${row.label}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    showTooltip={false}
                    disabled={refusal !== null}
                    onClick={() => onRemove(row.role)}
                  />
                </Tooltip>
              </div>
            )}
          </li>
          );
        })}
      </ol>

      {readOnly || add === undefined ? null : (
        // ONE control, never a role picker: what it adds is the next document
        // the SERVICE's manifest allows and this asset does not carry yet, and
        // the tooltip names it — so a clinician learns the step by its document
        // name rather than choosing from a vocabulary.
        <Tooltip content={add.refusal ?? add.label}>
          <Button
            variant="outline"
            size="sm"
            disabled={add.refusal !== null}
            onClick={add.onAdd}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {add.label}
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * The read-only step list the CATALOG renders, from the contract's own shape.
 *
 * Separate from the authoring list above because it answers a different
 * question with different data: the catalog holds `PathwayStep[]` from
 * `GET /library/pathway-families` — one row per stored document, carrying the
 * server's `order` — while the editor holds a draft's `NamedRow[]`, one row per
 * ROLE across its languages. Rendering the catalog through the editor's list
 * would mean converting a response into a draft to display it, which mints
 * draft identities for content nobody is editing.
 */
export function PathwayStepSummary({
  steps,
  label,
}: {
  steps: readonly {
    id: string;
    role: string;
    kind: string;
    order: number;
    /**
     * The server's own verdict. Absent only for a caller passing draft rows,
     * which carry no such field; present on every contract step.
     */
    editability?: string;
  }[];
  /** What the list is OF, so its accessible name names the member. */
  label: string;
}) {
  if (steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This version carries no steps.
      </p>
    );
  }
  return (
    <ol aria-label={`Steps in ${label}, in clinical order`} className="space-y-1">
      {steps.map((step, index) => (
        <li
          key={step.id}
          className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
        >
          <span className="w-6 shrink-0 text-xs tabular-nums">{index + 1}.</span>
          <span className="min-w-0 truncate text-foreground">
            {pathwayStepLabel(step.role)}
          </span>
          {/* THE SERVER'S VERDICT, not the document kind. Since #911 a
              structured step may publish a typed schema, and badging it locked
              would tell a clinician a form does not exist when the service says
              it does. The form itself is #912's; what this row must not do is
              contradict the contract it renders. */}
          {(step.editability ??
            (step.kind === "structured" ? "protected" : "editable")) ===
          "protected" ? (
            <Badge tone="outline">
              <Lock aria-hidden="true" className="mr-1 h-3 w-3" />
              Clinical engine document
            </Badge>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
